import type { QuoteData, QuoteTemplate } from '@better-lights/core';
import { Link } from '@tanstack/react-router';
import { FileText } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAccount } from '../account/store.ts';
import { updateQuote } from '../app/projectActions.ts';
import { has } from '../plans/features.ts';
import { openPaywallFor, useFlags } from '../plans/gate.ts';
import { errorMessage, toast } from '../state/feedback.ts';
import { useLibrary } from '../state/library.ts';
import { useSync } from '../sync/service.ts';
import { Button } from '../ui/Button.tsx';
import { Checkbox } from '../ui/Checkbox.tsx';
import { Field } from '../ui/Field.tsx';
import { LockBadge } from '../ui/LockBadge.tsx';
import { Section } from '../ui/Section.tsx';
import { Segmented } from '../ui/Segmented.tsx';
import { type GeneratedQuote, generateQuote } from './generate.ts';
import { quoteDataOf } from './model.ts';
import { QuotePreview } from './QuotePreview.tsx';

function TextArea({
  label,
  value,
  onChange,
  rows = 3,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="t-caption text-muted">{label}</span>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-sm border border-border bg-surface2 px-3 py-2 text-[15px] text-text outline-none focus:border-accent"
      />
    </label>
  );
}

/** "Quote" section of a project: customer, notes, template, deposit, preview and share. */
export function QuotePanel({ projectId }: { projectId: string }) {
  const file = useLibrary((l) => l.projects.find((p) => p.id === projectId));
  const scope = useSync((s) => s.rows[projectId]?.scope);
  const activeScope = useAccount((a) => a.activeScope);
  const personal = useAccount((a) => a.me?.personalScope);
  const flags = useFlags(scope ?? activeScope);
  const [q, setQ] = useState<QuoteData>(() => (file ? quoteDataOf(file) : {}));
  const [aiPage, setAiPage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<GeneratedQuote | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once per project
  useEffect(() => {
    if (file) setQ(quoteDataOf(file));
  }, [projectId]);

  const save = (patch: Partial<QuoteData>, immediate = false) => {
    const next = { ...q, ...patch };
    setQ(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => void updateQuote(projectId, patch), immediate ? 0 : 500);
  };
  const customer = q.customer ?? {};
  const setCustomer = (k: keyof NonNullable<QuoteData['customer']>, v: string) =>
    save({ customer: { ...customer, [k]: v } });
  const templates = has(flags, 'quote_templates');
  const branded = has(flags, 'branded_quote');
  const orgProject = !!scope && !!personal && scope !== personal;

  return (
    <div className="flex flex-col gap-6">
      <Section title="Prepared for">
        <Field
          label="Name"
          value={customer.name ?? ''}
          onChange={(e) => setCustomer('name', e.target.value)}
          placeholder="e.g. The Smiths"
        />
        <TextArea label="Address" value={customer.address ?? ''} onChange={(v) => setCustomer('address', v)} rows={2} />
        <div className="grid grid-cols-1 gap-3 min-[600px]:grid-cols-2">
          <Field
            label="Email"
            type="email"
            value={customer.email ?? ''}
            onChange={(e) => setCustomer('email', e.target.value)}
          />
          <Field
            label="Phone"
            type="tel"
            value={customer.phone ?? ''}
            onChange={(e) => setCustomer('phone', e.target.value)}
          />
        </div>
      </Section>
      <Section title="Notes">
        <TextArea label="Shown on the quote" value={q.notes ?? ''} onChange={(v) => save({ notes: v })} rows={4} />
      </Section>
      <Section title="Layout" right={templates ? null : <LockBadge flag="quote_templates" />}>
        <div
          onClickCapture={(e) => {
            if (templates) return;
            e.preventDefault();
            e.stopPropagation();
            openPaywallFor('quote_templates');
          }}
        >
          <Segmented<QuoteTemplate>
            label="Quote layout"
            value={templates ? (q.template ?? 'classic') : 'classic'}
            onChange={(t) => save({ template: t }, true)}
            options={[
              { value: 'classic', label: 'Classic' },
              { value: 'modern', label: 'Modern' },
              { value: 'minimal', label: 'Minimal' },
            ]}
          />
        </div>
        {templates ? (
          <>
            <Field
              label="Deposit"
              inputMode="decimal"
              suffix="%"
              className="w-40"
              value={q.depositPercent ?? ''}
              onChange={(e) => {
                const v = Number.parseFloat(e.target.value.replace(',', '.'));
                save({ depositPercent: Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : null });
              }}
              placeholder="none"
            />
            <Checkbox
              checked={aiPage}
              onChange={setAiPage}
              label="Add a page with AI renders"
              caption="The latest render for each time of day."
            />
          </>
        ) : null}
      </Section>
      <div className="flex items-center justify-between gap-3 rounded-md bg-surface2 px-3 py-2">
        <span className="t-caption text-dim">
          {orgProject && flags.company_branding
            ? 'Uses your company’s branding, set by your admins.'
            : branded
              ? 'Uses your logo, colors and terms.'
              : 'Basic quote. Add your logo, colors and terms with a paid plan.'}
        </span>
        {branded && !orgProject ? (
          <Link to="/branding" className="t-label shrink-0 text-accent">
            Edit branding
          </Link>
        ) : !branded ? (
          <button type="button" onClick={() => openPaywallFor('branded_quote')}>
            <LockBadge flag="branded_quote" />
          </button>
        ) : null}
      </div>
      <Button
        variant="primary"
        size="lg"
        icon={FileText}
        loading={busy}
        onClick={async () => {
          setBusy(true);
          try {
            clearTimeout(timer.current);
            await updateQuote(projectId, q);
            setPreview(await generateQuote(projectId, { includeAiRenders: templates && aiPage }));
          } catch (e) {
            toast(errorMessage(e, 'Could not create the quote.'), 'error');
          } finally {
            setBusy(false);
          }
        }}
      >
        Preview quote PDF
      </Button>
      <QuotePreview quote={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
